import React, { useState, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Programs from './components/Programs';
import SupportingTeam from './components/SupportingTeam';
import Marquee from './components/Marquee';
import Footer from './components/Footer';
import ResetPassword from './components/ResetPassword';
import FAQ from './components/FAQ';
import CertificateVerify from './components/CertificateVerify';

const Login = React.lazy(() => import('./components/Login'));
const Register = React.lazy(() => import('./components/Register'));
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const Classroom = React.lazy(() => import('./components/Classroom'));
const RegistrationQR = React.lazy(() => import('./components/RegistrationQR'));

// 1. Group the landing page components into one reusable layout
const LandingPage = ({ showPrograms, handleNavigation }) => (
  <>
    <Hero onExplore={() => handleNavigation('pricing')} />
    <About />
    <Programs visible={showPrograms} onNavigate={handleNavigation} />
    <FAQ />
    <SupportingTeam />
    <Marquee />
    
    <Footer />
  </>
);

// 2. The core App component where we use navigation hooks
function AppContent() {
  const navigate = useNavigate();
  const showPrograms = true; // Programs section visibility on landing page

  // THE FIX: Synchronous State Initialization.
  const [isLoggedIn, setIsLoggedIn] = useState(!!sessionStorage.getItem('access_token'));
  const [userName, setUserName] = useState(sessionStorage.getItem('user_name') || '');
  
  const [activeCourseId, setActiveCourseId] = useState(
    sessionStorage.getItem('active_course_id') ? parseInt(sessionStorage.getItem('active_course_id')) : null
  );

  // Handle a successful login
  const handleLoginSuccess = (name) => {
    setIsLoggedIn(true);
    setUserName(name);
    navigate('/dashboard'); 
    window.scrollTo(0, 0);
  };

  // Handle logout
  const handleLogout = () => {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user_name');
    setIsLoggedIn(false);
    setUserName('');
    navigate('/'); 
  };

  // Unified Navigation Handler 
  const handleNavigation = (section, data = null) => {
    if (section === 'login') {
      navigate('/login');
      window.scrollTo(0, 0);
    } else if (section === 'register') {
      navigate('/register');
      window.scrollTo(0, 0);
    } else if (section === 'registration') {
      navigate('/registration');
      window.scrollTo(0, 0);
    } else if (section === 'dashboard') {
      navigate('/dashboard');
      window.scrollTo(0, 0);
    } else if (section === 'classroom') {
      setActiveCourseId(data);
      sessionStorage.setItem('active_course_id', data); 
      navigate('/classroom');
      window.scrollTo(0, 0);
    } else if (section === 'home') {
      navigate('/');
      window.scrollTo(0, 0);
    } else if (section === 'advance') {
      alert('Advanced Programs section coming soon!');
    } else {
      // For scrollable sections (programs, about, pricing), ensure we are on the home URL first
      navigate('/');
      setTimeout(() => {
        let targetClass = '.programs';
        if (section === 'about') targetClass = '.about-section';
        else if (section === 'pricing') targetClass = '.pricing-section-container';
        
        const element = document.querySelector(targetClass);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <div className="App">
      <Header 
        onNavigate={handleNavigation} 
        isLoggedIn={isLoggedIn} 
        userName={userName}
        onLogout={handleLogout}
      />

      <Suspense fallback={<div className="route-loading">Loading...</div>}>
        <Routes>
          {/* Landing Page Route */}
          <Route path="/" element={<LandingPage showPrograms={showPrograms} handleNavigation={handleNavigation} />} />
          
          
          {/* Auth Routes */}
          <Route 
            path="/login" 
            element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <Login onLoginSuccess={handleLoginSuccess} />} 
          />
          <Route 
            path="/register" 
            element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <Register />} 
          />
          <Route 
            path="/registration" 
            element={<RegistrationQR />} 
          />
          
          {/* --- NEW PASSWORD RESET ROUTE --- */}
          {/* The dynamic URL parameters map directly to your ResetPassword component */}
          <Route 
            path="/reset-password/:uidb64/:token" 
            element={<ResetPassword />} 
          />
          {/* -------------------------------- */}

          {/* Protected Dashboard Route */}
          <Route 
            path="/dashboard" 
            element={
              isLoggedIn ? (
                <Dashboard userName={userName} onNavigate={handleNavigation} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />

          {/* Protected Classroom Route */}
          <Route 
            path="/classroom" 
            element={
              isLoggedIn ? (
                <Classroom courseId={activeCourseId} onNavigate={handleNavigation} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />

          {/* The Certificate Verification Route */}
          <Route path="/verify/:certificateId" element={<CertificateVerify />} />
        </Routes>
      </Suspense>
    </div>
  );
}

// 4. Wrap everything in BrowserRouter
export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}